import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import SearchableSelect from '@/components/SearchableSelect';
import { toast } from 'sonner';

const Num = ({ value, onChange, testid }) => (
  <Input type="number" value={value ?? 0} onChange={e => onChange(Number(e.target.value) || 0)} className="h-7 text-xs w-32 text-right" data-testid={testid} />
);

function SectionEditor({ s, idx, upd, branches }) {
  const set = (patch) => upd(idx, { ...s, ...patch });
  const setClinic = (gi, ci, patch) => set({
    groups: s.groups.map((g, i) => i !== gi ? g : { ...g, clinics: g.clinics.map((c, j) => j !== ci ? c : { ...c, ...patch }) }),
  });
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`dcr-cfg-section-${s.key}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-[#0F4C81] w-40">{s.title || s.groups?.map(g => g.catchment).join(', ')}</span>
        {s.kind !== 'ho' && <>
          <label>VP</label><Input value={s.vp || ''} onChange={e => set({ vp: e.target.value })} className="h-7 text-xs w-36" data-testid={`dcr-cfg-vp-${s.key}`} />
          <Input value={s.head_label || ''} onChange={e => set({ head_label: e.target.value })} className="h-7 text-xs w-52" />
          <Input value={s.head || ''} onChange={e => set({ head: e.target.value })} className="h-7 text-xs w-48" data-testid={`dcr-cfg-head-${s.key}`} />
        </>}
        {(s.kind === 'ci' || s.kind === 'virtual') && <><label>Target</label><Num value={s.target} onChange={v => set({ target: v })} testid={`dcr-cfg-target-${s.key}`} /></>}
      </div>
      {s.kind === 'ho' && s.rows.map((r, ri) => (
        <div key={r.label} className="flex items-center gap-2 text-xs pl-4">
          <span className="w-40">{r.label} ({r.invoice_type})</span>
          <Num value={r.target} onChange={v => set({ rows: s.rows.map((x, k) => k === ri ? { ...x, target: v } : x) })} testid={`dcr-cfg-target-${s.key}-${ri}`} />
        </div>
      ))}
      {s.groups?.map((g, gi) => g.clinics.map((c, ci) => (
        <div key={`${gi}-${ci}`} className="flex items-center gap-2 text-xs pl-4">
          <span className="w-24 text-[#64748B]">{ci === 0 ? g.catchment : ''}</span>
          <Input value={c.label} onChange={e => setClinic(gi, ci, { label: e.target.value })} className="h-7 text-xs w-48" />
          <SearchableSelect value={c.location || ''} onChange={v => setClinic(gi, ci, { location: v })} placeholder="Map to location…"
            options={branches.map(b => ({ value: b, label: b }))} testid={`dcr-cfg-loc-${s.key}-${gi}-${ci}`} buttonClassName="h-7 text-xs w-64" />
          <Num value={c.target} onChange={v => setClinic(gi, ci, { target: v })} testid={`dcr-cfg-target-${s.key}-${gi}-${ci}`} />
        </div>
      )))}
    </div>
  );
}

export function TargetsDialog({ month, onClose, onSaved }) {
  const [sections, setSections] = useState(null);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/reports/daily-clinic-revenue/config', { params: { month } }).then(r => setSections(r.data.sections)).catch(() => toast.error('Failed to load targets'));
    api.get('/billing/admin/branches').then(r => setBranches((r.data.branches || []).map(b => b.name))).catch(() => {});
  }, [month]);
  const upd = (i, s) => setSections(prev => prev.map((x, k) => (k === i ? s : x)));
  const save = async () => {
    setSaving(true);
    try { await api.put('/reports/daily-clinic-revenue/config', { month, sections }); toast.success(`Targets saved for ${month}`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="dcr-targets-dialog">
        <DialogHeader><DialogTitle>Targets & Heads — {month}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#64748B]">Saved targets apply to this month and are carried forward to later months until changed.</p>
        <div className="space-y-3">{sections?.map((s, i) => <SectionEditor key={s.key} s={s} idx={i} upd={upd} branches={branches} />)}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dcr-targets-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !sections} className="bg-[#0F4C81] text-white" data-testid="dcr-targets-save">{saving ? 'Saving…' : 'Save Targets'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
